import type { ProfessionalPlanRow } from '@/services/professional-plans-service';

/**
 * Catalog-resolved display copy for a paid professional tier, sourced from
 * `plan_translations` / `plan_features` (per CLAUDE.md plan-catalog rule 2:
 * "display names live in plan_translations"). Every field is optional — an
 * absent field means the catalog hasn't backfilled it, and the caller should
 * fall back to the `Lawyers.pricingPage.*` i18n key.
 */
interface CatalogTierCopy {
  name?: string | undefined;
  tagline?: string | undefined;
  features?: string[] | undefined;
}

/**
 * Resolve a DB plan row's display copy for `locale`, mirroring the resolution
 * `ProfessionalPricingSection` does inline so the two professional surfaces
 * render plan copy identically:
 *
 *   - `name` / `tagline` from `translations[locale]`, falling back to the `en`
 *     translation. `short_tagline` is nullable in the DB → a null tagline
 *     resolves to `undefined` (caller shows the i18n tagline, not an empty
 *     string).
 *   - `features` from `features[]`, ordered by `display_order`, each bullet
 *     resolved `locale → en → ''`, with empties dropped. An empty resulting
 *     list resolves to `undefined` so the caller uses its i18n feature keys.
 *
 * A field is only set when the catalog actually provides it, so the caller's
 * `?? t(key)` fallback chain stays correct for un-backfilled rows.
 */
export function resolveCatalogTierCopy(
  plan: ProfessionalPlanRow,
  locale: string
): CatalogTierCopy {
  const tr = plan.translations?.[locale] ?? plan.translations?.['en'];
  const features = (plan.features ?? [])
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((f) => f.translations[locale] ?? f.translations['en'] ?? '')
    .filter((text) => text.length > 0);

  return {
    name: tr?.display_name,
    tagline: tr?.short_tagline ?? undefined,
    features: features.length > 0 ? features : undefined,
  };
}
